/**
 * Token Paste Authentication Service
 * 
 * Allows users to paste a JWT token directly for authentication.
 * Used for testing with real nonprod/prod tokens.
 * 
 * USE CASE: Testing with real backend APIs, nonprod environment
 * 
 * @module services/auth/TokenPasteAuthService
 */

import { AuthTokens, IAuthService } from '@/types/auth';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import { decodeJWT, isTokenExpired } from '@/utils/jwt';

export class TokenPasteAuthService implements IAuthService {
  private static readonly STORAGE_KEY = 'token_paste_auth_token';
  private static readonly USER_ID_KEY = 'token_paste_user_id';

  /**
   * Login with email/password - Not supported in token paste mode
   */
  async login(_email: string, _password: string): Promise<AuthTokens> {
    throw new Error('Email/password login not supported in token paste mode. Please use loginWithToken().');
  }

  /**
   * Login with JWT token
   * 
   * Validates the token format and expiry, then stores it.
   * 
   * @param token - JWT token string from nonprod/prod
   * @returns Token wrapped in AuthTokens
   */
  async loginWithToken(token: string): Promise<AuthTokens> {
    console.log('🎫 [TokenPasteAuth] Login with token');

    // Validate token format
    if (!token || typeof token !== 'string') {
      throw new Error('Invalid token: Token must be a non-empty string');
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid token format: JWT must have 3 parts (header.payload.signature)');
    }

    // Decode and validate token
    const claims = decodeJWT(token);
    if (!claims) {
      throw new Error('Invalid token: Failed to decode JWT');
    }

    // Check if token is expired
    if (isTokenExpired(token)) {
      throw new Error('Token is expired. Please get a fresh token.');
    }

    // Extract user ID
    const userId = claims.ref_user_id;
    if (!userId) {
      throw new Error('Invalid token: Missing ref_user_id claim');
    }

    // Store token
    this.storeToken(token, userId);

    console.log('✅ [TokenPasteAuth] Token login successful:', {
      userId,
      email: claims.email,
      issuer: claims.iss,
      expiresAt: new Date(claims.exp * 1000).toISOString(),
    });

    return {
      accessToken: token,
      expiresIn: claims.exp - Math.floor(Date.now() / 1000),
    };
  }

  /**
   * Logout - clears stored token
   */
  async logout(): Promise<void> {
    console.log('🎫 [TokenPasteAuth] Logout');
    
    localStorage.removeItem(TokenPasteAuthService.STORAGE_KEY);
    localStorage.removeItem(TokenPasteAuthService.USER_ID_KEY);
    CommunicationBridge.setAccessToken('');
  }

  /**
   * Get current access token
   */
  async getAccessToken(): Promise<string | null> {
    return localStorage.getItem(TokenPasteAuthService.STORAGE_KEY);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(TokenPasteAuthService.STORAGE_KEY);
    if (!token) return false;
    
    // Check if token is expired
    return !isTokenExpired(token);
  }

  /**
   * Store token in localStorage and CommunicationBridge
   */
  private storeToken(token: string, userId: string): void {
    localStorage.setItem(TokenPasteAuthService.STORAGE_KEY, token);
    localStorage.setItem(TokenPasteAuthService.USER_ID_KEY, userId);
    localStorage.setItem('access_token', token); // Also store in CommunicationBridge's key
    CommunicationBridge.setAccessToken(token);
  }
}

